import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { UI } from "@wwimmo/ui";
import { runInAction } from "mobx";
import { selectRoute, Route } from "src/config/routes";
import { RootStoreContext } from "src/stores/RootStore";
import styles from "./MfaSettings.module.css";

export const MfaSettings = () => {
    const { t } = useTranslation();
    const { navStore, authStore } = useContext(RootStoreContext);
    const [mfaEnabled, setMfaEnabled] = useState<boolean>(authStore.user?.mfaEnabled ?? false);
    const [mfaToggleError, setMfaToggleError] = useState<Error | undefined>(undefined);
    // TODO: Implement MFA with mobile number (SMS)
    // const [smsChecked, setSmsChecked] = useState<boolean>(false);
    // const [mobileNumber, setMobileNumber] = useState<string>("");

    const isMfaToggleDisabled = authStore.user?.hasInspectionRole ?? false;

    useEffect(() => {
        runInAction(() => {
            navStore.setTitle(t("screens.auth.mfa.title"));
            navStore.setBackbutton(selectRoute(Route.profile), t("screens.account.title").toString());
        });
    }, [navStore, t]);

    const onToggleMfa = useCallback(async () => {
        if (isMfaToggleDisabled) {
            return;
        }

        const newMfaEnabled = !mfaEnabled;
        setMfaEnabled(newMfaEnabled);
        setMfaToggleError(undefined);

        try {
            await authStore.authClient.toggleMfa(newMfaEnabled);
        } catch (toggleError: any) {
            setMfaEnabled(!newMfaEnabled);
            setMfaToggleError(toggleError);
        }
    }, [authStore.authClient, mfaEnabled, isMfaToggleDisabled]);

    return (
        <UI.Container className="pt-4">
            <UI.Row>
                <UI.Col xs={{ span: 8, offset: 2 }} lg={{ span: 6, offset: 3 }}>
                    <UI.Card className={`mb-3 ${styles.MfaCard}`} title={t("screens.auth.mfa.short_title").toString()}>
                        <div className={styles.MfaCardSwitch}>
                            {isMfaToggleDisabled ? (
                                <UI.OverlayTrigger
                                    placement="left"
                                    overlay={
                                        <UI.Tooltip id="mfaToggle-tooltip">
                                            {t("screens.auth.mfa.toggle_disabled_tooltip")}
                                        </UI.Tooltip>
                                    }
                                >
                                    <span>
                                        <UI.Switch
                                            checked={mfaEnabled}
                                            onToggle={onToggleMfa}
                                            disabled={isMfaToggleDisabled}
                                        />
                                    </span>
                                </UI.OverlayTrigger>
                            ) : (
                                <UI.Switch checked={mfaEnabled} onToggle={onToggleMfa} disabled={isMfaToggleDisabled} />
                            )}
                        </div>
                    </UI.Card>
                    <UI.Card className={styles.MfaCard} title={t("screens.auth.mfa.options.title").toString()}>
                        <UI.FormGroup className="mb-3">
                            <UI.Checkbox
                                name="mfaOptionEmail"
                                label={`${t("screens.auth.mfa.options.email").toString()} [${
                                    authStore.user?.username ?? ""
                                }]`}
                                checked={mfaEnabled}
                                onChange={() => {}}
                                disabled={!mfaEnabled}
                            />
                            {/* TODO: Implement MFA with mobile number (SMS) */}
                            {/* <UI.Checkbox
                                name="mfaOptionSms"
                                label={t("screens.auth.mfa.options.sms").toString()}
                                checked={smsChecked}
                                onChange={() => setSmsChecked(!smsChecked)}
                            /> */}
                        </UI.FormGroup>
                        {/* TODO: Implement MFA with mobile number (SMS) */}
                        {/* <UI.FormGroup className="mb-3">
                            <UI.Form.Label>{t("screens.auth.mfa.options.mobile_number")}</UI.Form.Label>
                            <UI.Form.Control
                                value={mobileNumber}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    setMobileNumber(event.target.value)
                                }
                                placeholder={t("screens.auth.mfa.options.mobile_number_placeholder").toString()}
                            />
                        </UI.FormGroup>
                        <UI.Button className="mt-4" label={t("labels.save")} /> */}
                    </UI.Card>
                    {mfaToggleError && (
                        <UI.Alert variant="danger" className="mt-2">
                            {mfaToggleError.message}
                        </UI.Alert>
                    )}
                </UI.Col>
            </UI.Row>
        </UI.Container>
    );
};
